import React, { useEffect, useState, useCallback } from 'react';
import { useSnackbar } from 'notistack';
import {
  Box,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  TablePagination,
  MenuItem,
  CircularProgress,
  Card,
  Tooltip,
  FormControl,
  Select,
  Button
} from '@mui/material';
import CloudDownloadIcon from '@mui/icons-material/CloudDownload';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { useQuery, useLazyQuery, gql } from '@apollo/client';
import * as XLSX from 'xlsx';
import MapVinDialog from './MapVinDialog';
import AssignVinDialog from './AssignVinDialog';
import EditDetailsDialog from './EditDetailsDialog';
import ManualKycDialog from './ManualKycDialog';
import VinNoChangeDialog from './VinNoChangeDialog';
import TelematicCheckDialog from './TelematicCheckDialog';
import EoltReportViewDialog from '../EoltReports/EoltReportViewDialog';
import { useAuth } from '../../hooks/useAuth';
import Menu from '@mui/material/Menu';
import FilterListIcon from '@mui/icons-material/FilterList';

const GET_DEVICE_DETAILS_BY_VIN = gql`
  query GetDeviceDetailsByVin($vin: String!) {
    getDeviceDetailsByVin(vin: $vin) {
      code
      message
      data {
        vin
        imei
        vinAssignedTime
        postVinFeed
        postVinFeedTime
        status
        deviceTS
        networkOperator
        boxCoverStatus
        address
        tcuBatlevel
        firmwareVersion
        serverTS
        latitude
        longitude
        gsmSignal
        ticketId
        ignitionTS
        vehBatVol
        fuelTemp
        adblueLevel
        odoMeter
        hourMeter
        dtc
        warningLight
        cleanerChokeIndicator
        lowOilPressure
        oilPassFilter
        dpfLampIndicator
        dpfActiveRegeneration
        dpfInhibitRegeneration
        massPercentage
        waterInFuelLevel
        torqueReduction
        speedReduction
        ecoMode
        engineRmp
        coolantTemp
        oilTemp
        gearIndicator
        createdByName
        createdById
        fileName
        helpButtonStatus
        packetType
      }
    }
  }
`;

const GET_ALL_VIN_IMEI_MAP_DATA = gql`
query GetAllVinImeiMapData($limit: Int, $offset: Int, $searchItem: String, $searchType: String, $status: String) {
  getAllVinImeiMapData(limit: $limit, offset: $offset, searchItem: $searchItem, searchType: $searchType, status: $status) {
    success
    data {
      vehicle_id
      vin
      imei
      iccid
      device_model
      device_status
      vin_imei_mapped_on
      engine_no
      vin_dealer_map
      customer_name
      customer_contact_no
      kyc_received_on
      kyc_approved_on
      kyc_rejected_on
      last_kyc_recieved
      ticket_created
      status
      kyc_mode
      rejection_reason
      sim_plan_type
      sim_subscription_start
      sim_subscription_end
      fileName
      dealer_id
      dealer_code
      dealer_name
      dealer_contact_no
      dealer_email
      dealer_address_line_1
    }
    count
    error
  }
}
`;


const cellStyle = {
  fontSize: '14px',
  padding: '8px 12px',
  borderBottom: '1px solid #e0e0e0',
  borderRight: '1px solid #e0e0e0',
  color: '#000',
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  maxWidth: '200px',
  textOverflow: 'ellipsis'
};

const columnStyles = {
  srNo: {
    ...cellStyle,
    maxWidth: '80px'
  },
  editDetails: {
    ...cellStyle,
    maxWidth: '140px',
  },
  manualKyc: {
    ...cellStyle,
    maxWidth: '140px',
  },
  VinNoChange: {
    ...cellStyle,
    maxWidth: '200px',
  },
};

interface VinJourneyData {
  vehicle_id: string;
  chassis_no: string;
  engine_no: string;
  imei: string;
  device_model: string;
  activation_status: string;
  vin_imei_mapped_on: number;
  vin_dealer_map: string;
  customer_name: string;
  customer_contact_number: string;
  kyc_received_on: number;
  kyc_approved_on: number;
  kyc_rejected_on: number;
  ticket_created: string;
  status: string;
  kyc_mode: string;
  rejection_reason: string;
  sim_plan_type: string;
  sim_subscription_start_date: number;
  sim_subscription_end_date: number;
  manual_eol: boolean;
  eol_report: string;
  last_packet: string;
  manual_kyc: boolean;
  vin_no_change: boolean;
  iccid?: string;
  fileName?: string;
  dealer_id: string;
  dealer_code: string;
  dealer_name: string;
  dealer_contact_no: string;
}

const formatDate = (timestamp: number): string => {
  if (!timestamp) return 'N/A';
  const date = new Date(timestamp * 1000);
  return date.toLocaleString();
};

const formatBoolean = (value: boolean): string => {
  return value ? 'Yes' : 'No';
};

const VIN_FILTER_OPTIONS = [
  { label: 'All', value: 'ALL' },
  { label: 'VIN-IMEI Mapped', value: 'VIN_IMEI_MAPPED' },
  { label: 'KYC_RECEIVED', value: 'KYC_RECEIVED' },
  { label: 'IMEI_NOT_MAPPED', value: 'IMEI_NOT_MAPPED' },
  { label: 'KYC_APPROVED', value: 'KYC_APPROVED' },
  { label: 'KYC_REJECTED', value: 'KYC_REJECTED' },
];

const VinJourney = () => {
  const { enqueueSnackbar } = useSnackbar();
  const auth = useAuth();
  const [manualKycDialogOpen, setManualKycDialogOpen] = useState(false);
  const [manualKycData, setManualKycData] = useState<{
    vehicleId: string;
    vin: string;
    imei: string;
    engine_no: string;
    iccid: string;
    device_model: string;
    vehicle_id: string;
    dealer_id: string;
    dealer_name: string;
    dealer_contact_no: string;
  } | null>(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [data, setData] = useState<VinJourneyData[]>([]);
  const [searchType, setSearchType] = useState('vin');
  const [searchTerm, setSearchTerm] = useState('');
  const [editDetailsDialogOpen, setEditDetailsDialogOpen] = useState(false);
  const [successSnackbar, setSuccessSnackbar] = useState({ open: false, message: '' });
  const [editDetailsData, setEditDetailsData] = useState<{
    vehicleId: string;
    vin: string;
    engineNo: string;
    imei: string;
  } | null>(null);
  const [mapVinOpen, setMapVinOpen] = useState(false);
  const [assignVinOpen, setAssignVinOpen] = useState(false);
  const [vinNoChangeDialogOpen, setVinNoChangeDialogOpen] = useState(false);
  const [vinNoChangeData, setVinNoChangeData] = useState<{
    vin: string;
    imei: string;
    iccid: string;
    vehicleId: string;
  } | null>(null);
  const [filterAnchorEl, setFilterAnchorEl] = useState<null | HTMLElement>(null);
  const [vinFilter, setVinFilter] = useState('ALL');
  const [totalCount, setTotalCount] = useState(0);
  const { data: queryData, loading, refetch } = useQuery(
    GET_ALL_VIN_IMEI_MAP_DATA,
    {
      variables: {
        offset: page * rowsPerPage,
        limit: rowsPerPage,
        searchItem: searchTerm.trim() || null,
        searchType: searchTerm ? searchType : null,
        status: vinFilter === 'ALL' ? null : vinFilter,
      },
      fetchPolicy: 'cache-and-network',
    }
  );

  const [getDeviceDetails, { loading: detailsLoading }] = useLazyQuery(GET_DEVICE_DETAILS_BY_VIN);
  const [telematicDialogOpen, setTelematicDialogOpen] = useState(false);
  const [telematicData, setTelematicData] = useState<any>(null);
  const [exportLoading, setExportLoading] = useState(false);
  const [getAllForExport] = useLazyQuery(GET_ALL_VIN_IMEI_MAP_DATA);
  const [reportViewDialogOpen, setReportViewDialogOpen] = useState(false);
  const [selectedReportData, setSelectedReportData] = useState<any>(null);

  const mapVinRow = useCallback((item: any): VinJourneyData => ({
    vehicle_id: item.vehicle_id || '',
    chassis_no: item.vin || '',
    engine_no: item.engine_no || '',
    imei: item.imei || '',
    device_model: item.device_model || '',
    activation_status: item.device_status || '',
    vin_imei_mapped_on: item.vin_imei_mapped_on || 0,
    vin_dealer_map: item.vin_dealer_map || '',
    customer_name: item.customer_name || '',
    customer_contact_number: item.customer_contact_no || '',
    kyc_received_on: item.kyc_received_on || 0,
    kyc_approved_on: item.kyc_approved_on || 0,
    kyc_rejected_on: item.kyc_rejected_on || 0,
    ticket_created: item.ticket_created || '',
    status: item.status || '',
    kyc_mode: item.kyc_mode || '',
    rejection_reason: item.rejection_reason || '',
    sim_plan_type: item.sim_plan_type || '',
    sim_subscription_start_date: item.sim_subscription_start || 0,
    sim_subscription_end_date: item.sim_subscription_end || 0,
    manual_eol: false,
    eol_report: '',
    last_packet: '',
    manual_kyc: false,
    vin_no_change: false,
    iccid: item.iccid || '',
    fileName: item.fileName || '',
    dealer_id: item.dealer_id || '',
    dealer_code: item.dealer_code || '',
    dealer_name: item.dealer_name || '',
    dealer_contact_no: item.dealer_contact_no || '',
  }), []);

  const handleTelematicCheck = async (item: VinJourneyData) => {
    if (!item.chassis_no) {
      enqueueSnackbar("VIN is missing for telematic check", { variant: 'warning' });
      return;
    }
    try {
      const response = await getDeviceDetails({ variables: { vin: item.chassis_no } });
      if (response.data?.getDeviceDetailsByVin?.data) {
        setTelematicData(response.data.getDeviceDetailsByVin.data);
        setTelematicDialogOpen(true);
      } else {
        // The server now says WHY -- an expired legacy token reads differently
        // from an unmapped VIN. Show its message, not a generic one.
        enqueueSnackbar(
          response.data?.getDeviceDetailsByVin?.message || "No telematics data returned",
          { variant: 'error' },
        );
      }
    } catch (error) {
      console.error(error);
      enqueueSnackbar(
        error instanceof Error ? error.message : "Error fetching telematic data",
        { variant: 'error' },
      );
    }
  };

  const handleReportLinkClick = (item: VinJourneyData) => {
    setSelectedReportData({
      reportId: '1',
      fileName: item.fileName || '',
      url: item.fileName || '',
      vin: item.chassis_no || '',
      createdOn: '',
      createdByName: '',
    });
    setReportViewDialogOpen(true);
  };

  useEffect(() => {
    if (queryData?.getAllVinImeiMapData?.success && Array.isArray(queryData.getAllVinImeiMapData.data)) {
      setData(queryData.getAllVinImeiMapData.data.map(mapVinRow));
      setTotalCount(queryData.getAllVinImeiMapData.count || 0);
    }
  }, [queryData, mapVinRow]);

  const handleEditClick = (item: VinJourneyData) => {
    setEditDetailsData({
      vehicleId: item.vehicle_id,
      vin: item.chassis_no || '',
      engineNo: item.engine_no || '',
      imei: item.imei || '',
    });
    setEditDetailsDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setEditDetailsDialogOpen(false);
    setEditDetailsData(null);
  };

  const handleEditDetailsDialogSuccess = (updatedValues: { vehicleId: string; imei: string; engineNo: string }) => {
    setData(prevData =>
      prevData.map(item =>
        item.vehicle_id === updatedValues.vehicleId
          ? {
            ...item,
            imei: updatedValues.imei,
            engine_no: updatedValues.engineNo
          }
          : item
      )
    );
    setEditDetailsDialogOpen(false);
    setEditDetailsData(null);
  };

  const handleMapVinOpen = () => {
    setMapVinOpen(true);
  };

  const handleAssignVinOpen = () => {
    setAssignVinOpen(true);
  };

  const handleMapVinClose = () => {
    setMapVinOpen(false);
  };

  const handleExportToExcel = async () => {
    setExportLoading(true);
    try {
      const { data: exportResponse } = await getAllForExport({
        variables: { limit: 999999, offset: 0 },
      });

      if (!exportResponse?.getAllVinImeiMapData?.success) {
        enqueueSnackbar("Failed to fetch records for export", { variant: 'error' });
        return;
      }

      const allRecords = exportResponse.getAllVinImeiMapData.data || [];

      if (allRecords.length === 0) {
        enqueueSnackbar("No records found to export", { variant: 'info' });
        return;
      }

      const formattedData = allRecords.map((item: any, index: number) => ({
        'Sr.No': index + 1,
        'VIN': item.vin || "N/A",
        'IMEI': item.imei || "N/A",
        'Device Status': item.device_status || "N/A",
        'Device Model': item.device_model || "N/A",
        'VIN IMEI Mapped On': formatDate(item.vin_imei_mapped_on),
        'Engine No.': item.engine_no || "N/A",
        'ICCID': item.iccid || "N/A",
        'VIN Dealer Map': item.vin_dealer_map || "N/A",
        'Customer Name': item.customer_name || "N/A",
        'Customer Contact Number': item.customer_contact_no || "N/A",
        'KYC Received On': formatDate(item.kyc_received_on),
        'KYC Approved On': formatDate(item.kyc_approved_on),
        'KYC Rejected On': formatDate(item.kyc_rejected_on),
        'Status': item.status || "N/A",
        'KYC mode': item.kyc_mode || "N/A",
        'Rejection Reason': item.rejection_reason || "N/A",
        'Sim Plan Type': item.sim_plan_type || "N/A",
        'Sim Subscription Start': formatDate(item.sim_subscription_start),
        'Sim Subscription End': formatDate(item.sim_subscription_end),
        'Manual EOL': "N/A",
        'EOL Report': item.fileName ? 'Generated' : 'N/A',
        'Last Packet': "N/A",
      }));

      const ws = XLSX.utils.json_to_sheet(formattedData);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "VIN Journeys");

      XLSX.writeFile(wb, `VIN_Journey_All_Records_${new Date().toISOString().slice(0, 10)}.xlsx`);
    } catch (error) {
      console.error("Export error:", error);
      enqueueSnackbar("Export failed. Please try again.", { variant: 'error' });
    } finally {
      setExportLoading(false);
    }
  };
  useEffect(() => {
    setPage(0);
  }, [searchTerm, searchType, vinFilter]);

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  function handleKycClick(item: VinJourneyData): void {
    setManualKycData({
      vehicleId: item.vehicle_id || '',
      vin: item.chassis_no || '',
      imei: item.imei || '',
      engine_no: item.engine_no || '',
      iccid: item.iccid || '',
      device_model: item.device_model || '',
      vehicle_id: item.vehicle_id || '',
      dealer_id: item.dealer_id || '',
      dealer_name: item.dealer_name || '',
      dealer_contact_no: item.dealer_contact_no || '',
    });
    setManualKycDialogOpen(true);
  }

  const handleManualKycDialogSubmit = (formData: any) => {
    setManualKycDialogOpen(false);
    setManualKycData(null);
    if (typeof refetch === 'function') refetch();
  };

  const handleVinNoChangeClick = (item: VinJourneyData) => {
    setVinNoChangeData({
      vin: item.chassis_no || '',
      imei: item.imei || '',
      iccid: item.iccid || '',
      vehicleId: item.vehicle_id || ''
    });
    setVinNoChangeDialogOpen(true);
  };

  const handleVinNoChangeDialogClose = () => {
    setVinNoChangeDialogOpen(false);
    setVinNoChangeData(null);
  };

  const handleVinNoChangeDialogSubmit = ({ vehicleId, newVin }: { vehicleId: string; newVin: string }) => {
    setData(prevData =>
      prevData.map(item =>
        item.vehicle_id === vehicleId
          ? { ...item, chassis_no: newVin }
          : item
      )
    );

  };



  const tableData = data;
  const isLoading = loading;
  const paginationCount = totalCount;

  return (
    <Box sx={{ width: '100%', px: { xs: 0, sm: 0 } }}>
      <Typography variant="h5" fontSize={{ xs: '1.5rem', sm: '2.125rem' }} sx={{ mb: 2, borderRadius: 2 }}>
        VIN Journey
      </Typography>
      <Card sx={{
        width: '100%',
        mx: 'auto',
        boxShadow: 2,
        borderRadius: 3,
        p: 0,
        display: 'flex',
        flexDirection: 'column',
        height: { xs: 'auto', md: 'calc(100vh - 180px)' },
        minHeight: 100,
        bgcolor: '#fff',
        flex: 1,
      }}>
        {manualKycData && (
          <ManualKycDialog
            open={manualKycDialogOpen}
            onClose={() => {
              setManualKycDialogOpen(false);
              setManualKycData(null);
              refetch();
            }}
            data={manualKycData}
            vehicleId={manualKycData.vehicleId}
          />
        )}
        {/* {isLoading && <LinearProgress />} */}

        <Box sx={{
          position: 'sticky',
          top: 0,
          zIndex: 20,
          background: '#fff',
          borderTopLeftRadius: 8,
          borderTopRightRadius: 8,
          boxShadow: '0 2px 8px -4px #e0e0e0',
          px: { xs: 1, sm: 2 },
          pt: 2,
          pb: 2,
          left: 0,
          right: 0,
          width: '100%',
          minWidth: 0,
          overflow: 'visible',
        }}>
          <Box
            display="flex"
            flexDirection={{ xs: 'column', sm: 'row' }}
            sx={{ justifyContent: 'space-between', alignItems: { xs: 'stretch', sm: 'center' }, gap: 2 }}
          >
            <Box
              display="flex"
              gap={1}
              alignItems="center"
              sx={{ minWidth: 0, flex: 1, maxWidth: { xs: '100%', sm: '40%' }, flexWrap: { xs: 'wrap', sm: 'nowrap' } }}
            >
              <FormControl size="small" sx={{ minWidth: 120, background: '#f5f5f5', flex: '0 0 auto' }}>
                <Select
                  value={searchType}
                  onChange={(e) => setSearchType(e.target.value)}
                  displayEmpty
                >
                  <MenuItem value="vin">Search by VIN</MenuItem>
                  <MenuItem value="engine_no">Search by Engine No</MenuItem>
                  <MenuItem value="imei">Search by IMEI</MenuItem>
                </Select>
              </FormControl>
              <TextField
                size="small"
                variant="outlined"
                placeholder={
                  searchType === 'engine_no' ? 'Search by Engine No' :
                    searchType === 'vin' ? 'Search by VIN' :
                      searchType === 'imei' ? 'Search by IMEI' : ''
                }
                value={searchTerm}
                onChange={(e) => {
                  let value = e.target.value;
                  if (searchType === 'imei') {
                    value = value.replace(/[^a-zA-Z0-9]/g, '').slice(0, 15);
                  } else if (searchType === 'vin') {
                    value = value.replace(/[^a-zA-Z0-9]/g, '').slice(0, 17);
                  } else {
                    value = value.slice(0, 32);
                  }
                  setSearchTerm(value);
                }}
                sx={{ background: "#f5f5f5", minWidth: 120, flex: 1 }}
                inputProps={{
                  maxLength: searchType === 'imei' ? 15 :
                    searchType === 'vin' ? 17 : 32
                }}
              />
              {/* Filter Icon and Dropdown */}
              <IconButton
                sx={{ ml: 1, color: '#000000ff', flex: '0 0 auto' }}
                onClick={e => setFilterAnchorEl(e.currentTarget)}
              >
                <FilterListIcon />
              </IconButton>
              <Menu
                anchorEl={filterAnchorEl}
                open={Boolean(filterAnchorEl)}
                onClose={() => setFilterAnchorEl(null)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
              >
                {VIN_FILTER_OPTIONS.map(option => (
                  <MenuItem
                    key={option.value}
                    selected={vinFilter === option.value}
                    onClick={() => {
                      setVinFilter(option.value);
                      setFilterAnchorEl(null);
                    }}
                  >
                    {option.label}
                  </MenuItem>
                ))}
              </Menu>
              <Tooltip title="Export to Excel" placement="top" arrow>
                <IconButton sx={{ ml: 1, color: '#000', flex: '0 0 auto' }} onClick={handleExportToExcel} disabled={exportLoading}>
                  {exportLoading ? <CircularProgress size={24} /> : <CloudDownloadIcon />}
                </IconButton>
              </Tooltip>
            </Box>
            {(auth.canCreate("VEHICLE") || auth.canUpdate("VEHICLE")) && (
              <>
                <Box display="flex" gap={3}>
                  {auth.canCreate("VEHICLE") && (
                    <>
                      <Button
                        variant="contained"
                        sx={{
                          color: '#fff',
                          backgroundColor: '#E60012',
                          '&:hover': { backgroundColor: '#cc0010' },
                          textTransform: 'none',
                          borderRadius: '8px',
                          paddingX: 2,
                          paddingY: 1,
                          minWidth: 120,
                          fontSize: { xs: '0.9rem', sm: '1rem' },
                        }}
                        onClick={handleMapVinOpen}
                      >
                        Map VIN
                      </Button>
                      <Button
                        variant="contained"
                        sx={{
                          color: '#fff',
                          backgroundColor: '#E60012',
                          '&:hover': { backgroundColor: '#cc0010' },
                          textTransform: 'none',
                          borderRadius: '8px',
                          paddingX: 2,
                          paddingY: 1,
                          minWidth: 120,
                          fontSize: { xs: '0.9rem', sm: '1rem' },
                        }}
                        onClick={handleAssignVinOpen}
                      >
                        Assign VIN
                      </Button>
                    </>
                  )}
                </Box>
              </>
            )}
          </Box>
        </Box>

        <Box sx={{ flex: 1, minHeight: 0, px: { xs: 0.5, sm: 2 }, py: 2, overflow: 'auto', display: 'flex', flexDirection: 'column' }}>
          <Box sx={{ width: '100%', overflowX: 'auto' }}>
            <TableContainer
              component={Paper}
              sx={{
                boxShadow: 'none',
                borderRadius: 2,
                width: '100%',
                minWidth: 600,
                background: '#fff',
                overflow: 'visible',
              }}
            >
              <Table
                stickyHeader
                size="small"
                sx={{
                  minWidth: 600,
                  width: '100%',
                  tableLayout: 'auto',
                  borderCollapse: 'separate',
                  borderSpacing: 0,
                }}
              >
                <TableHead>
                  <TableRow sx={{ position: 'sticky', top: 0, zIndex: 10, backgroundColor: '#E60012' }}>
                    <TableCell sx={{
                      ...columnStyles.srNo, backgroundColor: '#E60012', color: '#fff', position: 'sticky', top: 0, zIndex: 11, fontWeight: 'bold',
                      borderRight: '1px solid #e0e0e0', // Explicit border
                      boxShadow: '1px 0 0 0 rgba(224, 224, 224, 0.7) inset' // Subtle right shadow
                    }} align="center">Sr.No</TableCell>
                    <TableCell sx={{
                      ...cellStyle, backgroundColor: '#E60012', color: '#fff', position: 'sticky', top: 0, zIndex: 11, fontWeight: 'bold',
                      borderRight: '1px solid #e0e0e0',
                      boxShadow: '1px 0 0 0 rgba(224, 224, 224, 0.7) inset'
                    }} align="center">VIN</TableCell>
                    <TableCell sx={{
                      ...cellStyle, backgroundColor: '#E60012', color: '#fff', position: 'sticky', top: 0, zIndex: 11, fontWeight: 'bold',
                      borderRight: '1px solid #e0e0e0',
                      boxShadow: '1px 0 0 0 rgba(224, 224, 224, 0.7) inset'
                    }} align="center">IMEI</TableCell>
                    <TableCell sx={{
                      ...cellStyle, backgroundColor: '#E60012', color: '#fff', position: 'sticky', top: 0, zIndex: 11, fontWeight: 'bold',
                      borderRight: '1px solid #e0e0e0',
                      boxShadow: '1px 0 0 0 rgba(224, 224, 224, 0.7) inset'
                    }} align="center">Device Status</TableCell>
                    <TableCell sx={{
                      ...cellStyle, backgroundColor: '#E60012', color: '#fff', position: 'sticky', top: 0, zIndex: 11, fontWeight: 'bold',
                      borderRight: '1px solid #e0e0e0',
                      boxShadow: '1px 0 0 0 rgba(224, 224, 224, 0.7) inset'
                    }} align="center">VIN IMEI Mapped On</TableCell>
                    <TableCell sx={{
                      ...cellStyle, backgroundColor: '#E60012', color: '#fff', position: 'sticky', top: 0, zIndex: 11, fontWeight: 'bold',
                      borderRight: '1px solid #e0e0e0',
                      boxShadow: '1px 0 0 0 rgba(224, 224, 224, 0.7) inset'
                    }} align="center">Engine No.</TableCell>
                    <TableCell sx={{
                      ...cellStyle, backgroundColor: '#E60012', color: '#fff', position: 'sticky', top: 0, zIndex: 11, fontWeight: 'bold',
                      borderRight: '1px solid #e0e0e0',
                      boxShadow: '1px 0 0 0 rgba(224, 224, 224, 0.7) inset'
                    }} align="center">ICCID</TableCell>
                    <TableCell sx={{
                      ...cellStyle, backgroundColor: '#E60012', color: '#fff', position: 'sticky', top: 0, zIndex: 11, fontWeight: 'bold',
                      borderRight: '1px solid #e0e0e0',
                      boxShadow: '1px 0 0 0 rgba(224, 224, 224, 0.7) inset'
                    }} align="center">Device Model</TableCell>
                    <TableCell sx={{
                      ...cellStyle, backgroundColor: '#E60012', color: '#fff', position: 'sticky', top: 0, zIndex: 11, fontWeight: 'bold',
                      borderRight: '1px solid #e0e0e0',
                      boxShadow: '1px 0 0 0 rgba(224, 224, 224, 0.7) inset'
                    }} align="center">VIN Dealer Map</TableCell>
                    <TableCell sx={{
                      ...cellStyle, backgroundColor: '#E60012', color: '#fff', position: 'sticky', top: 0, zIndex: 11, fontWeight: 'bold',
                      borderRight: '1px solid #e0e0e0',
                      boxShadow: '1px 0 0 0 rgba(224, 224, 224, 0.7) inset'
                    }} align="center">Customer Name</TableCell>
                    <TableCell sx={{
                      ...cellStyle, backgroundColor: '#E60012', color: '#fff', position: 'sticky', top: 0, zIndex: 11, fontWeight: 'bold',
                      borderRight: '1px solid #e0e0e0',
                      boxShadow: '1px 0 0 0 rgba(224, 224, 224, 0.7) inset'
                    }} align="center">Customer Contact Number</TableCell>
                    <TableCell sx={{
                      ...cellStyle, backgroundColor: '#E60012', color: '#fff', position: 'sticky', top: 0, zIndex: 11, fontWeight: 'bold',
                      borderRight: '1px solid #e0e0e0',
                      boxShadow: '1px 0 0 0 rgba(224, 224, 224, 0.7) inset'
                    }} align="center">KYC Received On</TableCell>
                    <TableCell sx={{
                      ...cellStyle, backgroundColor: '#E60012', color: '#fff', position: 'sticky', top: 0, zIndex: 11, fontWeight: 'bold',
                      borderRight: '1px solid #e0e0e0',
                      boxShadow: '1px 0 0 0 rgba(224, 224, 224, 0.7) inset'
                    }} align="center">KYC Approved On</TableCell>
                    <TableCell sx={{
                      ...cellStyle, backgroundColor: '#E60012', color: '#fff', position: 'sticky', top: 0, zIndex: 11, fontWeight: 'bold',
                      borderRight: '1px solid #e0e0e0',
                      boxShadow: '1px 0 0 0 rgba(224, 224, 224, 0.7) inset'
                    }} align="center">KYC Rejected On</TableCell>
                    <TableCell sx={{
                      ...cellStyle, backgroundColor: '#E60012', color: '#fff', position: 'sticky', top: 0, zIndex: 11, fontWeight: 'bold',
                      borderRight: '1px solid #e0e0e0',
                      boxShadow: '1px 0 0 0 rgba(224, 224, 224, 0.7) inset'
                    }} align="center">Status</TableCell>
                    <TableCell sx={{
                      ...cellStyle, backgroundColor: '#E60012', color: '#fff', position: 'sticky', top: 0, zIndex: 11, fontWeight: 'bold',
                      borderRight: '1px solid #e0e0e0',
                      boxShadow: '1px 0 0 0 rgba(224, 224, 224, 0.7) inset'
                    }} align="center">KYC mode</TableCell>
                    <TableCell sx={{
                      ...cellStyle, backgroundColor: '#E60012', color: '#fff', position: 'sticky', top: 0, zIndex: 11, fontWeight: 'bold',
                      borderRight: '1px solid #e0e0e0',
                      boxShadow: '1px 0 0 0 rgba(224, 224, 224, 0.7) inset'
                    }} align="center">Rejection Reason</TableCell>
                    <TableCell sx={{
                      ...cellStyle, backgroundColor: '#E60012', color: '#fff', position: 'sticky', top: 0, zIndex: 11, fontWeight: 'bold',
                      borderRight: '1px solid #e0e0e0',
                      boxShadow: '1px 0 0 0 rgba(224, 224, 224, 0.7) inset'
                    }} align="center">Sim Plan Type</TableCell>
                    <TableCell sx={{
                      ...cellStyle, backgroundColor: '#E60012', color: '#fff', position: 'sticky', top: 0, zIndex: 11, fontWeight: 'bold',
                      borderRight: '1px solid #e0e0e0',
                      boxShadow: '1px 0 0 0 rgba(224, 224, 224, 0.7) inset'
                    }} align="center">Sim Subscription Start</TableCell>
                    <TableCell sx={{
                      ...cellStyle, backgroundColor: '#E60012', color: '#fff', position: 'sticky', top: 0, zIndex: 11, fontWeight: 'bold',
                      borderRight: '1px solid #e0e0e0',
                      boxShadow: '1px 0 0 0 rgba(224, 224, 224, 0.7) inset'
                    }} align="center">Sim Subscription End</TableCell>
                    <TableCell sx={{
                      ...cellStyle, backgroundColor: '#E60012', color: '#fff', position: 'sticky', top: 0, zIndex: 11, fontWeight: 'bold',
                      borderRight: '1px solid #e0e0e0',
                      boxShadow: '1px 0 0 0 rgba(224, 224, 224, 0.7) inset'
                    }} align="center">Manual EOL</TableCell>
                    <TableCell sx={{
                      ...cellStyle, backgroundColor: '#E60012', color: '#fff', position: 'sticky', top: 0, zIndex: 11, fontWeight: 'bold',
                      borderRight: '1px solid #e0e0e0',
                      boxShadow: '1px 0 0 0 rgba(224, 224, 224, 0.7) inset'
                    }} align="center">EOL Report</TableCell>
                    <TableCell sx={{
                      ...cellStyle, backgroundColor: '#E60012', color: '#fff', position: 'sticky', top: 0, zIndex: 11, fontWeight: 'bold',
                      borderRight: '1px solid #e0e0e0',
                      boxShadow: '1px 0 0 0 rgba(224, 224, 224, 0.7) inset'
                    }} align="center">Last Packet</TableCell>
                    {auth.canUpdate("VEHICLE") && (
                      <>
                        <TableCell sx={{
                          ...cellStyle, backgroundColor: '#E60012', color: '#fff', position: 'sticky', top: 0, zIndex: 11, fontWeight: 'bold',
                          borderRight: '1px solid #e0e0e0',
                          boxShadow: '1px 0 0 0 rgba(224, 224, 224, 0.7) inset'
                        }} align="center">Manual KYC</TableCell>
                        <TableCell sx={{
                          ...cellStyle, backgroundColor: '#E60012', color: '#fff', position: 'sticky', top: 0, zIndex: 11, fontWeight: 'bold',
                          borderRight: '1px solid #e0e0e0',
                          boxShadow: '1px 0 0 0 rgba(224, 224, 224, 0.7) inset'
                        }} align="center">Vin No. change</TableCell>
                        <TableCell sx={{
                          ...columnStyles.editDetails, backgroundColor: '#E60012', color: '#fff', position: 'sticky', top: 0, zIndex: 11, fontWeight: 'bold',
                        }} align="center">Edit Details</TableCell>
                      </>
                    )}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {isLoading ? (
                    <TableRow>
                      <TableCell colSpan={26} align="center" sx={{ py: 4 }}>
                        <CircularProgress size={24} />
                      </TableCell>
                    </TableRow>
                  ) : tableData.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={26} align="center" sx={{ py: 4, color: '#888' }}>
                        {searchTerm.trim() ? 'No results found for your search' : 'No VIN journeys found'}
                      </TableCell>
                    </TableRow>
                  ) : (
                    tableData.map((item, idx) => (
                      <TableRow
                        key={`${item.vehicle_id}-${idx}`}
                        hover
                        sx={{
                          backgroundColor: idx % 2 === 0 ? '#fff' : '#f9f9f9',
                          '&:hover': { backgroundColor: '#f0f0f0' }
                        }}
                      >
                        <TableCell align="center" sx={columnStyles.srNo}>
                          {page * rowsPerPage + idx + 1}
                        </TableCell>
                        <TableCell align="center" sx={cellStyle}>
                          <Tooltip title={item.chassis_no || "N/A"} placement="top" arrow>
                            <span>{item.chassis_no || "N/A"}</span>
                          </Tooltip>
                        </TableCell>
                        <TableCell align="center" sx={cellStyle}>
                          <Tooltip title={item.imei || "N/A"} placement="top" arrow>
                            <span>{item.imei || "N/A"}</span>
                          </Tooltip>
                        </TableCell>
                        <TableCell align="center" sx={cellStyle}>{item.activation_status || "N/A"}</TableCell>
                        <TableCell align="center" sx={cellStyle}>{item.vin_imei_mapped_on ? formatDate(item.vin_imei_mapped_on) : "N/A"}</TableCell>
                        <TableCell align="center" sx={cellStyle}>
                          <Tooltip title={item.engine_no || "N/A"} placement='top' arrow >
                            <span>{item.engine_no || "N/A"}</span></Tooltip></TableCell>
                        <TableCell align="center" sx={cellStyle}>
                          <Tooltip title={item.iccid || "N/A"} placement='top' arrow >
                            <span>{item.iccid || "N/A"}</span></Tooltip></TableCell>
                        <TableCell align="center" sx={cellStyle}>{item.device_model || "N/A"}</TableCell>
                        <TableCell align="center" sx={cellStyle}>{item.vin_dealer_map || "N/A"}</TableCell>
                        <TableCell align="center" sx={cellStyle}>{item.customer_name || "N/A"}</TableCell>
                        <TableCell align="center" sx={cellStyle}>{item.customer_contact_number || "N/A"}</TableCell>
                        <TableCell align="center" sx={cellStyle}>{item.kyc_received_on ? formatDate(item.kyc_received_on) : "N/A"}</TableCell>
                        <TableCell align="center" sx={cellStyle}>{item.kyc_approved_on ? formatDate(item.kyc_approved_on) : "N/A"}</TableCell>
                        <TableCell align="center" sx={cellStyle}>{item.kyc_rejected_on ? formatDate(item.kyc_rejected_on) : "N/A"}</TableCell>
                        <TableCell align="center" sx={cellStyle}>{item.status || "N/A"}</TableCell>
                        <TableCell align="center" sx={cellStyle}>{item.kyc_mode || "N/A"}</TableCell>
                        <TableCell align="center" sx={cellStyle}>
                          <Tooltip title={item.rejection_reason || "N/A"} placement='top' arrow>
                            <span>{item.rejection_reason || "N/A"}</span></Tooltip></TableCell>
                        <TableCell align="center" sx={cellStyle}>{item.sim_plan_type || "N/A"}</TableCell>
                        <TableCell align="center" sx={cellStyle}>{item.sim_subscription_start_date ? formatDate(item.sim_subscription_start_date) : "N/A"}</TableCell>
                        <TableCell align="center" sx={cellStyle}>{item.sim_subscription_end_date ? formatDate(item.sim_subscription_end_date) : "N/A"}</TableCell>
                        <TableCell align="center" sx={cellStyle}>
                          <CheckCircleIcon sx={{
                            color: item.fileName ? '#58B80F' : '#C0C0C0'
                          }} />
                        </TableCell>
                        <TableCell align="center" sx={cellStyle}>
                          <Button
                            variant="contained"
                            onClick={() => handleReportLinkClick(item)}
                            disabled={!item.fileName}
                            sx={{
                              backgroundColor: '#58B80F1A',
                              color: '#58B80F',
                              borderRadius: '12px',
                              height: '24px',
                              padding: '0 12px',
                              fontSize: '12px',
                              fontWeight: 'normal',
                              textTransform: 'none',
                              boxShadow: '0px 2px 4px rgba(0, 0, 0, 0.1)',
                              '&:hover': {
                                backgroundColor: '#58B80F33',
                              },
                              border: '1px solid #58B80F',
                              minWidth: '108px',
                              '&.Mui-disabled': {
                                backgroundColor: '#ccc',
                                color: '#666',
                                border: '1px solid #ccc',
                              },
                            }}
                          >
                            VIEW REPORT
                          </Button>
                        </TableCell>
                        <TableCell align="center" sx={cellStyle}>
                          <Button
                            variant="contained"
                            onClick={() => handleTelematicCheck(item)}
                            disabled={!!item.fileName}
                            sx={{
                              backgroundColor: '#58B80F1A',
                              color: '#58B80F',
                              borderRadius: '12px',
                              height: '24px',
                              padding: '0 12px',
                              fontSize: '12px',
                              fontWeight: 'normal',
                              textTransform: 'none',
                              boxShadow: '0px 2px 4px rgba(0, 0, 0, 0.1)',
                              '&:hover': {
                                backgroundColor: '#58B80F33',
                              },
                              border: '1px solid #58B80F',
                              minWidth: '108px',
                              '&.Mui-disabled': {
                                backgroundColor: '#ccc',
                                color: '#666',
                                border: '1px solid #ccc',
                              },
                            }}
                          >
                            TELEMATIC CHECK
                          </Button>
                        </TableCell>
                        {auth.canUpdate("VEHICLE") && (
                          <>
                            <TableCell align="center" sx={columnStyles.manualKyc}>
                              <Button
                                variant="contained"
                                onClick={() => handleKycClick(item)}
                                sx={{
                                  backgroundColor: '#58B80F1A',
                                  color: '#58B80F',
                                  borderRadius: '12px',
                                  height: '24px',
                                  padding: '0 12px',
                                  fontSize: '12px',
                                  fontWeight: 'normal',
                                  textTransform: 'none',
                                  boxShadow: '0px 2px 4px rgba(0, 0, 0, 0.1)',
                                  '&:hover': {
                                    backgroundColor: '#58B80F33',
                                  },
                                  border: '1px solid #58B80F',
                                  minWidth: '108px',
                                }}
                              >
                                MANUAL KYC
                              </Button>
                            </TableCell>

                            <TableCell align="center" sx={columnStyles.VinNoChange}>
                              <Button
                                variant="contained"
                                onClick={() => handleVinNoChangeClick(item)}
                                sx={{
                                  backgroundColor: '#58B80F1A',
                                  color: '#58B80F',
                                  borderRadius: '12px',
                                  height: '24px',
                                  padding: '0 12px',
                                  fontSize: '12px',
                                  fontWeight: 'normal',
                                  textTransform: 'none',
                                  boxShadow: '0px 2px 4px rgba(0, 0, 0, 0.1)',
                                  '&:hover': {
                                    backgroundColor: '#58B80F33',
                                  },
                                  border: '1px solid #58B80F',
                                  minWidth: '108px',
                                }}
                              >
                                VIN NO. CHANGE
                              </Button>
                            </TableCell>
                            <TableCell align="center" sx={columnStyles.editDetails}>
                              <Button
                                variant="contained"
                                onClick={() => handleEditClick(item)}
                                sx={{
                                  backgroundColor: '#58B80F1A',
                                  color: '#58B80F',
                                  borderRadius: '12px',
                                  height: '24px',
                                  padding: '0 12px',
                                  fontSize: '12px',
                                  fontWeight: 'normal',
                                  textTransform: 'none',
                                  boxShadow: '0px 2px 4px rgba(0, 0, 0, 0.1)',
                                  '&:hover': {
                                    backgroundColor: '#58B80F33',
                                  },
                                  border: '1px solid #58B80F',
                                  minWidth: '108px',
                                }}
                              >
                                EDIT DETAILS
                              </Button>
                            </TableCell>
                          </>
                        )}
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        </Box>

        <Box sx={{
          position: 'sticky',
          bottom: 0,
          zIndex: 10,
          background: '#fff',
          borderTop: '1px solid #e0e0e0',
          py: 1,
          px: 2
        }}>
          <TablePagination
            rowsPerPageOptions={[20, 50, 100, 500]}
            component="div"
            count={paginationCount}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            labelRowsPerPage="Rows per page:"
            sx={{
              '.MuiTablePagination-toolbar': {
                px: 0,
                flexWrap: 'wrap',
                justifyContent: 'center',
                '@media (min-width: 600px)': {
                  flexWrap: 'nowrap',
                  justifyContent: 'space-between'
                }
              }
            }}
          />
        </Box>
      </Card>

      <MapVinDialog
        open={mapVinOpen}
        onClose={handleMapVinClose}
        onSuccess={refetch}
      />
      <AssignVinDialog
        vins={data.map(item => item.chassis_no)}
        open={assignVinOpen}
        onClose={() => setAssignVinOpen(false)}
        onSuccess={() => {
          setAssignVinOpen(false);
          refetch();
        }}
      />

      {editDetailsData && (
        <EditDetailsDialog
          open={editDetailsDialogOpen}
          onClose={handleCloseDialog}
          onSuccess={handleEditDetailsDialogSuccess}
          data={editDetailsData}

        />
      )}

      {vinNoChangeData && (
        <VinNoChangeDialog
          open={vinNoChangeDialogOpen}
          onClose={handleVinNoChangeDialogClose}
          onSubmit={handleVinNoChangeDialogSubmit}
          data={vinNoChangeData}
        />
      )}

      <TelematicCheckDialog
        open={telematicDialogOpen}
        onClose={() => setTelematicDialogOpen(false)}
        data={telematicData}
      />

      <EoltReportViewDialog
        open={reportViewDialogOpen}
        onClose={() => setReportViewDialogOpen(false)}
        reportData={selectedReportData}
      />
    </Box>
  );
};

export default VinJourney;